<template>
  <StepLayout
    ref="stepRef"
    :title="steps[currentStep]?.title"
    :count="count"
    :showBack="!editing"
    :withSearch="steps[currentStep]?.withSearch && !componentRef?.empty"
    class="flex flex-col p-4 gap-1"
    @back="$emit('back')"
  >
    <template #button="{ isMobile }">
      <ActionButton
        :variant="variant"
        :disabled="editing && !isDirty"
        :hide="!variant || !canUpdate"
        :buildingId="building.id"
        :isMobile="isMobile"
        @clear="componentRef?.clear"
        @goToStep="handleGoToStep"
      />
    </template>
    <Component
      ref="componentRef"
      :is="steps[currentStep]?.content"
      v-bind="steps[currentStep]?.props"
      @update:item="$emit('update:item', $event)"
      @back="handleStepBack"
    />
  </StepLayout>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { ActionButton, StepLayout } from "@/SharedComponents/Passport";
import {
  FileList,
  FileSelect,
  FileUpload,
} from "@/SharedComponents/Passport/Steps/Files";
import { useSteps, Steps } from "@/Composables/useSteps";
import { useI18n } from "vue-i18n";

defineEmits(["back", "update:item"]);

const stepRef = ref(null);
const componentRef = ref(null);

const props = defineProps({
  item: { type: Object, required: true },
  building: { type: Object, required: true },
  canUpdate: { type: Boolean, required: false },
});

const { t } = useI18n();

const steps = computed(
  () =>
    new Steps({
      overview: {
        title: t("admin.files.label", 2),
        content: FileList,
        props: {
          item: props.item,
          canUpdate: props.canUpdate,
        },
      },
      upload: {
        title: t("components.passport.files.actions.upload"),
        content: FileUpload,
        props: {
          item: props.item,
          building: props.building,
        },
      },
      select: {
        title: t("components.passport.files.actions.select"),
        withSearch: true,
        content: FileSelect,
        props: {
          item: props.item,
          building: props.building,
          query: stepRef.value?.query,
        },
      },
    })
);

const {
  currentStep,
  handleGoToStep,
  handleStepBack: _handleStepBack,
  reset,
} = useSteps(steps.value);

function handleStepBack() {
  stepRef.value?.clearQuery();
  _handleStepBack();
}

onMounted(() => {
  reset();
});

const count = computed(() => {
  switch (currentStep.value) {
    case "overview":
      return props.item.files.length;
    default:
      return undefined;
  }
});

const variant = computed(() => {
  switch (currentStep.value) {
    case "overview":
      return "add-file";
    case "select":
      return "clear";
    default:
      return undefined;
  }
});

const editing = computed(() =>
  ["upload", "select"].includes(currentStep.value)
);

const isDirty = computed(() => componentRef.value?.isDirty);

defineExpose({
  editing,
  isDirty,
  cancel: () => componentRef.value?.cancel(),
  submit: () => componentRef.value?.submit(),
});
</script>
